import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  MenuItem,
  Typography,
  Box
} from '@mui/material';
import { SessionTransferStudent } from './sessionTransferApi';

interface SessionTransferTableProps {
  students: SessionTransferStudent[];
  selectedStudents: string[];
  onToggleSelect: (studentId: string) => void;
  onToggleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  classes: any[];
  sections: Record<string, any[]>; // classId -> sections[]
  transferData: Record<string, { newClassId: string; newSectionId: string }>;
  onUpdateTransferData: (studentId: string, field: 'newClassId' | 'newSectionId', value: string) => void;
}

const SessionTransferTable: React.FC<SessionTransferTableProps> = ({
  students,
  selectedStudents,
  onToggleSelect,
  onToggleSelectAll,
  classes,
  sections,
  transferData,
  onUpdateTransferData
}) => {
  if (students.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">No students found for the selected filters.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '60vh' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                checked={students.length > 0 && selectedStudents.length === students.length}
                onChange={onToggleSelectAll}
              />
            </TableCell>
            <TableCell>Admission No</TableCell>
            <TableCell>Scholar No</TableCell>
            <TableCell>Student Name</TableCell>
            <TableCell>Current Class</TableCell>
            <TableCell>Current Section</TableCell>
            <TableCell>New Class</TableCell>
            <TableCell>New Section</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {students.map((student) => {
            const isSelected = selectedStudents.indexOf(student.id) !== -1;
            const currentTransferData = transferData[student.id] || { newClassId: '', newSectionId: '' };
            const classSections = sections[currentTransferData.newClassId] || [];

            return (
              <TableRow key={student.id} selected={isSelected} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect(student.id)}
                  />
                </TableCell>
                <TableCell>{student.admissionNumber}</TableCell>
                <TableCell>{student.scholarNumber || '-'}</TableCell>
                <TableCell>{`${student.firstName} ${student.lastName}`}</TableCell>
                <TableCell>{student.currentClass.name}</TableCell>
                <TableCell>{student.currentSection.name}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={currentTransferData.newClassId}
                    onChange={(e) => onUpdateTransferData(student.id, 'newClassId', e.target.value)}
                  >
                    {classes.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    disabled={!currentTransferData.newClassId}
                    value={currentTransferData.newSectionId}
                    onChange={(e) => onUpdateTransferData(student.id, 'newSectionId', e.target.value)}
                  >
                    {classSections.map((sec) => (
                      <MenuItem key={sec.id} value={sec.id}>
                        {sec.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SessionTransferTable;
